import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { CheckIcon, GridIcon, BadgeIcon, ShieldIcon, TimeIcon } from './icons';
import './styles.scss';

// Competitor facts come from each vendor's public website (October 2026).
// Keep claims to what the vendors publish themselves, and refresh the
// footnote date whenever this table changes.
const competitors = [
  { key: 'procore', name: 'Procore' },
  { key: 'forma', name: 'Autodesk Forma Build', note: 'Formerly Autodesk Build' },
  { key: 'fieldwire', name: 'Fieldwire', note: 'By Hilti' }
];

const rows = [
  {
    label: 'Published pricing',
    pl: 'Yes. CA$39/user/mo or CA$599/mo flat fee per project',
    procore: 'Custom quote only',
    forma: 'Per-user list prices. Unlimited-user plans by quote',
    fieldwire: 'US$39 to US$89/user/mo, billed annually'
  },
  {
    label: 'What sets your price',
    pl: 'Users or projects. Never the size of the job',
    procore: 'Your annual construction volume. Users are unlimited',
    forma: 'Number of seats',
    fieldwire: 'Number of seats and plan tier'
  },
  {
    label: 'Contract terms',
    pl: 'Month-to-month. No annual contracts',
    procore: 'Upfront annual fee',
    forma: 'Lowest rates require annual billing',
    fieldwire: 'Listed rates require annual billing'
  },
  {
    label: 'RFIs and submittals',
    pl: 'Included on every plan',
    procore: 'Included in Project Management',
    forma: 'Included',
    fieldwire: 'Top Business Plus tier only'
  },
  {
    label: 'Safety, traffic control and compliance',
    pl: 'Fully integrated with SafetyLoop, TrafficLoop and ComplyLoop',
    procore: 'Quality & Safety is a separately priced product',
    forma: 'Forms and checklists inside Forma Build',
    fieldwire: 'Custom forms from the Business tier up'
  },
  {
    label: 'Free trial',
    pl: '90 days, no credit card. 6 months for SafetyLoop customers',
    procore: 'Demo by request',
    forma: '30-day trial',
    fieldwire: 'Free Basic plan for up to 5 users and 3 projects'
  },
  {
    label: 'Headquarters',
    pl: 'Ontario, Canada, with Canadian data residency',
    procore: 'United States',
    forma: 'United States (Autodesk)',
    fieldwire: 'United States (Hilti Group)'
  }
];

const reasons = [
  {
    icon: GridIcon,
    title: 'Your price never grows with the job',
    body: 'A $200M tower costs the same CA$599/mo as a $5M renovation. Volume-based pricing climbs with every contract you win. Ours stays put.'
  },
  {
    icon: BadgeIcon,
    title: 'Every tool on every plan',
    body: 'Drawings, RFIs, submittals, daily logs, punch lists and forms come with the first seat. No tier upgrades to unlock the basics.'
  },
  {
    icon: ShieldIcon,
    title: 'Safety, traffic and compliance built in',
    body: 'Fully integrated with SafetyLoop, TrafficLoop and ComplyLoop on a single login. No point solutions to stitch together.'
  },
  {
    icon: TimeIcon,
    title: 'No annual contracts',
    body: 'Month-to-month in Canadian dollars. Scale up for a busy season and scale back when it ends, without renegotiating.'
  }
];

export default function CompetitorSection() {
  return (
    <section className="plh-versus" id="how-we-compare">
      <Reveal className="plh-lead">
        <p className="plh-eyebrow">How ProjectLoop compares</p>
        <h2>
          <span className="plh-chapter">04 —</span> The big-platform toolkit.{' '}
          <span className="plh-accent">Without the big-platform bill.</span>
        </h2>
        <p className="plh-lead__sub">
          Procore, Autodesk Forma Build and Fieldwire are capable platforms. ProjectLoop gives
          Canadian builders the same core project tools with public CAD pricing, no annual
          contracts, and safety, traffic control and compliance fully integrated.
        </p>
      </Reveal>

      <Reveal className="plh-versus__table" delay={80}>
        <div
          className="plh-versus__scroll"
          role="region"
          aria-label="ProjectLoop compared with Procore, Autodesk Forma Build and Fieldwire"
          // Focusable so keyboard users can scroll the wide table sideways.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}>
          <table>
            <caption className="plh-versus__caption">
              ProjectLoop compared with Procore, Autodesk Forma Build and Fieldwire
            </caption>
            <thead>
              <tr>
                <th scope="col" className="plh-versus__corner plh-mono">
                  What matters
                </th>
                <th scope="col" className="plh-versus__th-pl">
                  <span className="plh-versus__brand">ProjectLoop</span>
                  <span className="plh-versus__th-note plh-mono">Built in Ontario</span>
                </th>
                {competitors.map((c) => (
                  <th scope="col" key={c.key}>
                    <span className="plh-versus__brand">{c.name}</span>
                    {c.note && <span className="plh-versus__th-note plh-mono">{c.note}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label}>
                  <th scope="row" className="plh-versus__label">
                    {row.label}
                  </th>
                  <td className="plh-versus__pl">
                    <span className="plh-versus__check">
                      <CheckIcon />
                    </span>
                    {row.pl}
                  </td>
                  {competitors.map((c) => (
                    <td className="plh-versus__them" key={c.key}>
                      {row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      <div className="plh-versus__reasons">
        {reasons.map((reason, idx) => {
          const Icon = reason.icon;
          return (
            <Reveal className="plh-versus__reason" key={reason.title} delay={idx * 80}>
              <span className="plh-versus__reason-icon" aria-hidden="true">
                <Icon />
              </span>
              <h3>{reason.title}</h3>
              <p>{reason.body}</p>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="plh-versus__switch" delay={100}>
        <div className="plh-versus__switch-copy">
          <p className="plh-eyebrow plh-eyebrow--light">Thinking about switching?</p>
          <h3>Run your next project on ProjectLoop, free for 90 days.</h3>
          <p>
            No credit card, no commitment and no sales call. Already a SafetyLoop customer? Try it
            free for 6 months.
          </p>
        </div>
        <div className="plh-versus__switch-ctas">
          <Link to="/signup" className="pl-btn pl-btn--primary pl-btn--shine">
            Start 90-day free trial
          </Link>
          <Link to="/pricing" className="pl-btn pl-btn--outline-light">
            See full pricing
          </Link>
        </div>
      </Reveal>

      <Reveal as="p" className="plh-versus__footnote plh-mono" delay={140}>
        Competitor details reflect each vendor&rsquo;s public website as of October 2026 and may
        change. Fieldwire prices are US-dollar list prices billed annually. Procore, Autodesk, Forma
        and Fieldwire are trademarks of their respective owners, who are not affiliated with
        ProjectLoop.
      </Reveal>
    </section>
  );
}
